// ─── HelpOverlay.js ───────────────────────────────────────────────────────────
// Full-screen help overlay with 7 topic pages.
// Opens from ❓ button in header. Quick-reference cards, not long documentation.

import React, { useState, useEffect } from 'react';
import './HelpOverlay.css';

// ── Topic definitions ────────────────────────────────────────────────────────

const TOPICS = [
  {
    id: 'quickstart',
    icon: '🚀',
    title: 'Quick Start',
    subtitle: 'New to Site2Scope? Start here.',
    color: '#5B2D82',
    content: QuickStartContent,
  },
  {
    id: 'measure',
    icon: '📐',
    title: 'How to Measure',
    subtitle: 'BDM guide to measuring areas on site.',
    color: '#E8871B',
    content: MeasureContent,
  },
  {
    id: 'assess',
    icon: '📋',
    title: 'How to Assess',
    subtitle: 'PM guide to assessing measured areas.',
    color: '#059669',
    content: AssessContent,
  },
  {
    id: 'compare',
    icon: '⚖️',
    title: 'How to Compare Hours',
    subtitle: 'Reviewing measured vs assessed hours.',
    color: '#7c3aed',
    content: CompareContent,
  },
  {
    id: 'photos',
    icon: '📷',
    title: 'Photo Best Practices',
    subtitle: 'What to photograph and how.',
    color: '#0369a1',
    content: PhotoContent,
  },
  {
    id: 'surfaces',
    icon: '🧱',
    title: 'Surface Reference',
    subtitle: 'Quick lookup for surface types and settings.',
    color: '#b45309',
    content: SurfaceContent,
  },
  {
    id: 'faq',
    icon: '❓',
    title: 'FAQ',
    subtitle: 'Common questions answered.',
    color: '#475569',
    content: FAQContent,
  },
];

// ── Shared components ────────────────────────────────────────────────────────

function Tip({ children, icon }) {
  return (
    <div className="help-tip">
      <span className="help-tip-icon">{icon || '💡'}</span>
      <div className="help-tip-text">{children}</div>
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="help-step">
      <div className="help-step-num">{number}</div>
      <div className="help-step-body">
        <div className="help-step-title">{title}</div>
        <div className="help-step-desc">{children}</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="help-section">
      {title && <h3 className="help-section-title">{title}</h3>}
      {children}
    </div>
  );
}

function FAQItem({ q, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`help-faq-item ${open ? 'open' : ''}`}>
      <button className="help-faq-q" onClick={() => setOpen(o => !o)}>
        <span>{q}</span>
        <span className="help-faq-arrow">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="help-faq-a">{children}</div>}
    </div>
  );
}

// ── Page content ─────────────────────────────────────────────────────────────

function QuickStartContent() {
  return (
    <>
      <Section>
        <p className="help-intro">
          Site2Scope replaces tape measures and spreadsheets. Measure buildings on-site, capture photos, get Excel quotes and scope PDFs — all from your phone or tablet.
        </p>
      </Section>

      <Section title="The workflow">
        <div className="help-flow">
          <div className="help-flow-step">
            <span className="help-flow-icon">📐</span>
            <span className="help-flow-label">BDM Measures</span>
          </div>
          <span className="help-flow-arrow">→</span>
          <div className="help-flow-step">
            <span className="help-flow-icon">📋</span>
            <span className="help-flow-label">PM Assesses</span>
          </div>
          <span className="help-flow-arrow">→</span>
          <div className="help-flow-step">
            <span className="help-flow-icon">⚖️</span>
            <span className="help-flow-label">Compare Hours</span>
          </div>
          <span className="help-flow-arrow">→</span>
          <div className="help-flow-step">
            <span className="help-flow-icon">📄</span>
            <span className="help-flow-label">Export</span>
          </div>
        </div>
      </Section>

      <Section title="Your role">
        <p><strong>BDM / Estimator:</strong> Create projects, measure areas on-site, take photos, tag surfaces. Export Excel quotes and scope PDFs.</p>
        <p><strong>PM / Assessor:</strong> Review measured areas, assess surface conditions, enter estimated paint hours. Flag access needs and hazards.</p>
        <p><strong>Dual-role users</strong> can toggle between BDM and Assessor mode via the profile icon in the header — no need to log out.</p>
      </Section>

      <Section title="Getting started">
        <Step number="1" title="Create a project">
          Tap the + button on the project list. Enter the job number, client, address, and your name.
        </Step>
        <Step number="2" title="Add areas">
          Inside the project, tap "Add Internal" or "Add External". Name each area (e.g., "Level 1 Corridor", "North Elevation").
        </Step>
        <Step number="3" title="Measure surfaces">
          Tap surface pills to add them (Walls, Ceilings, Doors, etc.). Enter dimensions. Take photos.
        </Step>
        <Step number="4" title="Mark ready">
          When all areas are measured, tap "Ready to Assess". The PM will be notified.
        </Step>
      </Section>

      <Tip>Site2Scope works offline. Measurements save to your device and sync when you're back online.</Tip>
    </>
  );
}

function MeasureContent() {
  return (
    <>
      <Section title="Adding surfaces">
        <p>Tap the coloured pills at the top of the area form to add a surface. Each tap adds that surface type — tap again to add a second entry (e.g., two separate wall runs).</p>
        <p><strong>Custom surfaces:</strong> Tap the "✎ Custom Surface…" pill to name anything not in the standard list (e.g., "Metal Cladding", "Feature Wall"). It appears with that name in the Excel export and PDF.</p>
        <p><strong>Renaming:</strong> Custom surfaces and independent blocks can be renamed after saving. Tap the ✎ pencil icon next to the surface name (collapsed or expanded view) to edit it inline.</p>
      </Section>

      <Section title="Independent surface blocks">
        <p>If you need the same surface type twice with <strong>different settings</strong> (e.g., spray-painted feature walls alongside standard brush/roll walls), use the <strong>"+ Walls (new settings)"</strong> button at the bottom of the surface block. This creates an independent "Walls 2" block with its own speed, girth, coats, prep, and application method — calculated and exported separately.</p>
        <Tip>Independent blocks can also be renamed — tap the ✎ pencil to give them a descriptive label like "Feature Wall" or "South Elevation".</Tip>
      </Section>

      <Section title="Application method">
        <p>Each surface block has three method options:</p>
        <p><strong>Brush / Roll</strong> (default) — standard calculation, no adjustments.</p>
        <p><strong>🔫 Spray</strong> — automatically sets speed to 30 m²/hr and adds 25% downtime (setup, masking, clean-up). Downtime % is editable.</p>
        <p><strong>🧗 Abseil</strong> — automatically sets speed to 12 m²/hr and adds 40% downtime. Downtime % is editable.</p>
        <p>Non-default methods are labelled in the Excel export (e.g., "Walls (method=spray)"), the scope PDF, and the Compare Hours screen — so nothing is hidden from the estimator.</p>
      </Section>

      <Section title="Entering dimensions">
        <p>Each surface type has specific fields — length × height for walls, length × width for ceilings, quantity × width × height for doors, etc.</p>
        <Tip icon="⌨️">
          <strong>Speed tip:</strong> When entering measurements on your phone, use the <strong>tick/done button</strong> on the numeric keypad to confirm each field instead of tapping away from the keyboard. This keeps the keyboard open and auto-advances to the next field — much faster for entering multiple dimensions in sequence.
        </Tip>
        <p><strong>Quantity:</strong> Use the quantity field for identical items (e.g., 6 identical windows = enter dimensions once, set qty to 6).</p>
      </Section>

      <Section title="Repeat groups (compound blocks)">
        <p>For identical rooms or sections, create a <strong>repeat group</strong>. Measure the surfaces once, set the count (e.g., ×12 for 12 identical apartments), and every surface in the group is multiplied automatically.</p>
        <p>Each group gets a name that appears in the Excel export (e.g., "Group-Walls-x12").</p>
      </Section>

      <Section title="Wrap mode">
        <p>For continuous runs of the same surface (walls around a room, skirting, fascia), tap the wrap icon on the surface block. This lets you enter a shared dimension (e.g., room height) then quickly add each run length — 3m, 1.5m, 5m, etc.</p>
        <p>Available for: Walls, Skirting, Fascia, Soffit, Gutter.</p>
      </Section>

      <Section title="Doors and windows">
        <p><strong>Doors:</strong> Default dimensions are 0.9m × 2.1m. Change if needed. Toggle "Include frame" to add frame perimeter as a separate measurement. Choose "Both sides" if painting both faces.</p>
        <p><strong>Windows:</strong> Choose between frame perimeter (LM) for painting frames, or area (M²) for full window openings. Set quantity for identical windows.</p>
      </Section>

      <Section title="Surface settings">
        <p>Each surface has default production settings (coats, painting speed, girth, prep rate). These defaults match the Excel template. To override for a specific surface, expand the settings panel on the surface block.</p>
        <p>Changes here affect the in-app hour estimates. The Excel export picks up your custom settings too.</p>
      </Section>

      <Section title="Preliminary items">
        <p>At the bottom of each area form, tag any preliminary items that apply — Scaffolding, Traffic Management, Scissor Lift, Boom Lift, Security Fencing, Waste Removal, Permit Fees, Third Party Contractor, or a custom "Other" item.</p>
        <p>These are <strong>reminder tags only</strong> — no quantities or costs are entered in the app. The item names export to the Excel Take Off sheet so the estimator can fill in rates and quantities there.</p>
      </Section>

      <Section title="Scope notes, exclusions, and defects">
        <p>At the bottom of each area form:</p>
        <p><strong>Scope notes</strong> — what's included in this area's scope (appears in Excel and PDF).</p>
        <p><strong>Exclusions</strong> — what's explicitly out of scope (appears in Excel and PDF).</p>
        <p><strong>Defects note</strong> — surface condition issues the PM should know about.</p>
      </Section>

      <Section title="Marking ready to assess">
        <p>When all areas are measured, tap "Ready to Assess" in the workflow bar. If any areas are incomplete, you'll see a summary — you can fix them or proceed anyway. Incomplete areas are flagged so the PM knows they're intentional.</p>
      </Section>

      <Tip>Areas auto-finish when every surface has valid measurements. You don't need to manually mark each one — but the "Ready to Assess" gate checks for you just in case.</Tip>
    </>
  );
}

function AssessContent() {
  return (
    <>
      <Section title="Entering assess mode">
        <p>When a project is ready for assessment, tap "Enter Assess Mode" from the project detail screen. You can also enter assessment even while the BDM is still measuring — the workflow auto-advances.</p>
      </Section>

      <Section title="Per-surface hours">
        <p>Assessment is done per surface, not per area. For each surface in an area, enter your estimated paint hours based on the site conditions you observe.</p>
        <Tip icon="⌨️">
          <strong>Speed tip:</strong> On your phone, use the <strong>tick/done button</strong> on the numeric keypad after entering hours for each surface. This confirms the value and keeps your flow going — much faster than tapping off the keyboard for every entry.
        </Tip>
        <p>The rapid entry mode lets you type a total and split it — or just enter hours directly per surface.</p>
      </Section>

      <Section title="PM notes">
        <p>Each surface has a notes field. Use it to explain why your assessed hours differ from the calculated measurement — e.g., "Textured ceiling — slower application", "6 fire doors — extra coats", "Poor condition — significant prep".</p>
        <p>These notes appear in the Compare Hours screen so the BDM can see your reasoning.</p>
      </Section>

      <Section title="Adding missed surfaces">
        <p>If the BDM missed a surface, tap "Add missed surface" at the bottom of the area's surface list. Select the surface type — it will be flagged as "PM added" so the BDM knows to go back and measure it.</p>
      </Section>

      <Section title="Adding missing areas">
        <p>If an entire area was missed during measurement, tap "Add missing area" from the area picker. Enter a name and note. The area appears in the BDM's project with a "PM Added" badge.</p>
      </Section>

      <Section title="Access and safety tags">
        <p>For each area, tag the access needs and hazards — EWP, scaffold, asbestos, confined spaces, etc. These tags are based on Higgins WRA-HC016 and appear in the scope PDF.</p>
        <p>Tags are categorised by area type (internal access, external access, site hazards, surface prep, environmental). You can also add custom "Other" items.</p>
      </Section>

      <Section title="Washdown hours (external)">
        <p>For external areas, a separate washdown hours field captures prep washing time. This is kept separate from surface paint hours.</p>
      </Section>

      <Section title="Completing assessment">
        <p>When all areas are assessed, tap "Complete" to notify the BDM. You can save progress at any time and return later — your work is preserved.</p>
      </Section>

      <Tip>You can assess areas in any order. The progress bar at the top shows how many areas are complete. Areas with all surfaces assessed get a green tick.</Tip>
    </>
  );
}

function CompareContent() {
  return (
    <>
      <Section title="What is Compare Hours?">
        <p>Compare Hours shows measured hours (calculated from dimensions) and assessed hours (entered by the PM) side by side at the surface level. The BDM enters adjusted hours — the final figures for the project.</p>
        <p>Both BDMs and PMs can access Compare Hours. The BDM can edit adjusted hours; the PM sees a read-only view.</p>
      </Section>

      <Section title="The layout">
        <p><strong>Left panel:</strong> Area navigation. Click any area to see its detail. Traffic lights show the level of difference between measured and assessed.</p>
        <p><strong>Right panel:</strong> Per-surface table showing m², measured hours, assessed hours, the delta, and the adjusted hours input.</p>
      </Section>

      <Section title="Entering adjusted hours">
        <p>Adjusted hours are <strong>pre-populated with the PM's assessed figures</strong> — the assumption is the PM's assessment is usually close. You only need to change the ones you disagree with.</p>
        <p>For each surface row, you have three options:</p>
        <Step number="📏" title="Use measured">Tap the left button to fill with the calculated measured hours for that surface.</Step>
        <Step number="📋" title="Use assessed">Tap the right button to fill with the PM's assessed hours (the default).</Step>
        <Step number="✏️" title="Type your own">Click the input field and type any value.</Step>
        <p>The "Use All Measured" and "Use All Assessed" buttons at the top reset every surface in the current area.</p>
      </Section>

      <Section title="Traffic lights">
        <p>🟢 <strong>Green:</strong> Measured and assessed hours are within 15% — generally agree.</p>
        <p>🟡 <strong>Amber:</strong> 15–30% difference — worth discussing.</p>
        <p>🔴 <strong>Red:</strong> Over 30% difference — needs resolution.</p>
        <p>Check the PM notes column for context on why they assessed differently.</p>
      </Section>

      <Section title="Finalising">
        <p>Once every area has adjusted hours, the "Finalise Hours" button activates. This locks in the adjusted figures and moves the project to "Hours Adjusted" status.</p>
        <p>You can still re-open Compare Hours and make changes after finalising.</p>
      </Section>

      <Tip>Use "Amend Measurements" on any area to go back and fix the measured data. When you save, you'll return to Compare Hours — not the project screen.</Tip>
    </>
  );
}

function PhotoContent() {
  return (
    <>
      <Section title="What to photograph">
        <Step number="📸" title="Cover photo">
          One hero shot of the area — this appears on the project card, in the PDF cover page, and as the first photo in exports. Tap the star icon on a photo to set it as the cover.
        </Step>
        <Step number="🔍" title="Defect photos">
          Surface damage, cracks, peeling, water stains — anything the PM needs to see for assessment. Tap the ⚠ icon to tag a photo as a defect.
        </Step>
        <Step number="🧭" title="Directional photos">
          The compass auto-captures which direction you're facing (N, NE, E, etc.). This helps the PM understand orientation without being on-site. You can manually override the direction if the compass is inaccurate indoors.
        </Step>
      </Section>

      <Section title="What makes a useful photo">
        <p><strong>Show full surfaces:</strong> Step back far enough to capture the whole wall, ceiling, or elevation. Close-ups of defects are separate photos.</p>
        <p><strong>Include context:</strong> Doors, windows, and corners help the PM understand the space. A photo of just a blank wall is less useful than one showing the wall in relation to the room.</p>
        <p><strong>Cover all directions:</strong> For internal areas, photograph each wall direction. For external, photograph each elevation.</p>
        <p><strong>Lighting:</strong> Turn on lights for internal areas. For external, avoid shooting directly into the sun.</p>
      </Section>

      <Section title="Where photos appear">
        <p><strong>PDF scope report:</strong> The cover photo appears on the front page. Area photos appear on dedicated photo pages grouped by area.</p>
        <p><strong>Photo export (ZIP):</strong> All photos download as a structured ZIP with folders per area (INT_AreaName/, EXT_AreaName/) and filename suffixes (_COVER, _DEFECT).</p>
        <p><strong>Assessment:</strong> The PM sees all photos when assessing each area. Defect photos are highlighted.</p>
      </Section>

      <Tip>Photos compress automatically to save bandwidth. Take photos from the camera button inside Site2Scope rather than the phone's camera app — this ensures compass direction is captured and the photo is linked to the correct area.</Tip>
    </>
  );
}

function SurfaceContent() {
  return (
    <>
      <Section title="Internal surfaces">
        <div className="help-ref-table">
          <div className="help-ref-row help-ref-header">
            <span>Surface</span><span>Measurement</span><span>Unit</span><span>Notes</span>
          </div>
          <div className="help-ref-row"><span>Ceilings</span><span>L × W × Qty</span><span>m²</span><span>—</span></div>
          <div className="help-ref-row"><span>Walls</span><span>L × H × Qty</span><span>m²</span><span>Wrap mode available</span></div>
          <div className="help-ref-row"><span>Floors</span><span>L × W × Qty</span><span>m²</span><span>—</span></div>
          <div className="help-ref-row"><span>Windows</span><span>Frame W × L × Qty</span><span>m² or LM</span><span>Choose frame perimeter or area</span></div>
          <div className="help-ref-row"><span>Doors</span><span>Qty × W × H × Sides</span><span>m²</span><span>Optional frame. Default 0.9×2.1</span></div>
          <div className="help-ref-row"><span>Skirting</span><span>Length × Qty</span><span>LM</span><span>Wrap mode available</span></div>
          <div className="help-ref-row"><span>Stairs</span><span>L × W × Qty</span><span>m²</span><span>Optional landing</span></div>
        </div>
      </Section>

      <Section title="External surfaces">
        <div className="help-ref-table">
          <div className="help-ref-row help-ref-header">
            <span>Surface</span><span>Measurement</span><span>Unit</span><span>Notes</span>
          </div>
          <div className="help-ref-row"><span>Gutter</span><span>Length × Qty</span><span>LM</span><span>Wrap mode available</span></div>
          <div className="help-ref-row"><span>Fascia</span><span>Length × Qty</span><span>LM</span><span>Wrap mode available</span></div>
          <div className="help-ref-row"><span>Eaves</span><span>L × W × Qty</span><span>m²</span><span>—</span></div>
          <div className="help-ref-row"><span>Soffit</span><span>L × W × Qty</span><span>m²</span><span>Wrap mode available</span></div>
          <div className="help-ref-row"><span>Walls</span><span>L × H × Qty</span><span>m²</span><span>Covers Flat and Rough</span></div>
          <div className="help-ref-row"><span>Downpipes</span><span>Length × Qty</span><span>LM</span><span>—</span></div>
          <div className="help-ref-row"><span>Slab Edge</span><span>Length × Qty</span><span>LM</span><span>—</span></div>
          <div className="help-ref-row"><span>Doors</span><span>Qty × W × H</span><span>m²</span><span>—</span></div>
          <div className="help-ref-row"><span>Balustrade / Handrails</span><span>L × H × Qty</span><span>m²</span><span>—</span></div>
          <div className="help-ref-row"><span>Posts</span><span>Height × Qty</span><span>LM</span><span>Height × number of posts</span></div>
          <div className="help-ref-row"><span>Roofs</span><span>L × W × Qty</span><span>m²</span><span>Optional pitch factor</span></div>
        </div>
      </Section>

      <Section title="Surface settings">
        <p>Each surface has default production settings that match the Excel template:</p>
        <p><strong>Girth:</strong> Multiplier for painted area. Walls = 1.0 (full area), Skirting = 0.2 (narrow strip), etc.</p>
        <p><strong>Coats:</strong> Number of paint coats. Default is 2 for most surfaces, 3 for doors.</p>
        <p><strong>Speed:</strong> Painting rate in m²/hour. Varies by surface complexity.</p>
        <p><strong>Prep rate:</strong> Preparation rate in m²/hour. Default 75 for most surfaces.</p>
      </Section>

      <Section title="Application method">
        <p>Each surface block has an application method selector. This affects the formula, production rate, and downtime calculation:</p>
        <div className="help-ref-table">
          <div className="help-ref-row help-ref-header">
            <span>Method</span><span>Speed default</span><span>Downtime</span><span>Use for</span>
          </div>
          <div className="help-ref-row"><span>Brush / Roll</span><span>User set</span><span>None</span><span>Standard work</span></div>
          <div className="help-ref-row"><span>🔫 Spray</span><span>30 m²/hr</span><span>25% (editable)</span><span>Open areas, ceilings</span></div>
          <div className="help-ref-row"><span>🧗 Abseil</span><span>12 m²/hr</span><span>40% (editable)</span><span>High-rise, rope access</span></div>
        </div>
        <p style={{marginTop:8}}>Downtime covers setup, masking, and cleanup — it's baked directly into the Excel formula so the estimator can see it. Non-default methods are labelled in Excel, the PDF, and Compare Hours.</p>
      </Section>

      <Tip>Custom surfaces and independent blocks (e.g. "Walls 2") can be renamed after saving — tap the ✎ pencil icon next to the surface name.</Tip>
    </>
  );
}

function FAQContent() {
  return (
    <>
      <Section title="Measurement">
        <FAQItem q="Can I rename a custom surface or independent block after saving?">
          Yes. Custom surfaces (anything you named yourself) and independent blocks like "Walls 2" show a ✎ pencil icon next to their name. Tap it to edit the name inline — press Enter or tap away to save. Standard surface types (Walls, Ceilings, Doors etc.) can't be renamed because their type drives the calculation formula.
        </FAQItem>
        <FAQItem q="What are Preliminary Items?">
          Preliminary items are site requirement tags — Scaffolding, Traffic Management, Scissor Lift, Boom Lift, Security Fencing, Waste Removal, Permit Fees, Third Party Contractor, or a custom "Other". They're tagged at the bottom of each area form as reminders. No quantities or costs are entered in the app — the item names export to Excel so the estimator can fill in the rates there.
        </FAQItem>
        <FAQItem q="When should I use 'new settings' vs adding another entry?">
          Use <strong>another entry</strong> (+ Walls) when the surface is the same type with the same settings — e.g., two separate wall runs in the same room at the same speed and coats. Use <strong>new settings</strong> (+ Walls (new settings)) when you need different speed, coats, girth, or application method — e.g., spray-painted feature walls alongside standard brush/roll walls. Both are exported to Excel as separate rows.
        </FAQItem>
        <FAQItem q="Can I edit measurements after marking 'Ready to Assess'?">
          Yes. Marking ready just notifies the PM — you can still open any area and edit measurements. The PM can also assess while you're still measuring.
        </FAQItem>
        <FAQItem q="Why does my area still show 'In progress'?">
          An area auto-finishes when every surface has valid measurements. Doors with default dimensions (0.9×2.1) don't count as measured — change the dimensions or quantity to mark them as measured. When you click "Ready to Assess", incomplete areas are shown so you can fix or accept them.
        </FAQItem>
        <FAQItem q="What happens when I duplicate an area?">
          A copy is created with the same surfaces, dimensions, and settings. Photos, notes, exclusions, defects, and all assessment data are cleared — the copy starts fresh.
        </FAQItem>
        <FAQItem q="How many areas can I have?">
          Up to 20 internal and 20 external areas per project. These match the Excel template's 40 area slots.
        </FAQItem>
        <FAQItem q="What's the difference between quantity and repeat groups?">
          <strong>Quantity</strong> is for identical items within one entry (e.g., 6 identical windows). <strong>Repeat groups</strong> are for identical rooms or sections where multiple surface types repeat together (e.g., 12 identical apartments each with walls, ceiling, doors, skirting).
        </FAQItem>
      </Section>

      <Section title="Assessment">
        <FAQItem q="Can the PM and BDM work on the same project at the same time?">
          Yes. The BDM edits measurements; the PM edits assessment fields. These are kept separate — a BDM save never overwrites assessment data, and vice versa. If both try to edit the same area, a lock indicator shows who's working on it.
        </FAQItem>
        <FAQItem q="What's the difference between assessed hours and adjusted hours?">
          <strong>Assessed hours</strong> are the PM's estimate per surface based on site conditions. <strong>Adjusted hours</strong> are the BDM's final decision per surface after comparing measured vs assessed — they're the figures used in the scope PDF.
        </FAQItem>
        <FAQItem q="Can the PM see the Compare Hours screen?">
          Yes — the PM sees a read-only view of Compare Hours. They can see measured, assessed, and adjusted figures side by side, but can't edit the adjusted hours.
        </FAQItem>
      </Section>

      <Section title="Export">
        <FAQItem q="What goes into the Excel export?">
          Only measured data — formulas, surface settings, metadata, notes, and exclusions. The Excel template calculates hours and costs from the measurements. Assessed and adjusted hours are not in the Excel file.
        </FAQItem>
        <FAQItem q="What goes into the Scope PDF?">
          Everything — project details, area breakdowns, surface measurements, hours (measured, assessed, or adjusted — you choose), photos, access needs, and notes. The cover page uses the project's hero photo.
        </FAQItem>
        <FAQItem q="Can I re-export after making changes?">
          Yes. Each export generates a new file from the current data. Nothing is locked after exporting.
        </FAQItem>
      </Section>

      <Section title="General">
        <FAQItem q="Does Site2Scope work offline?">
          Yes. Measurements save locally and sync to the server when you're back online. Photos queue for upload. You can work on-site with no connectivity.
        </FAQItem>

        <FAQItem q="Who can delete a project?">
          Only the person who created it. Anyone who has contributed (measured or assessed) can archive it.
        </FAQItem>
      </Section>
    </>
  );
}

// ── Main overlay component ───────────────────────────────────────────────────

export default function HelpOverlay({ onClose }) {
  const [activeTopic, setActiveTopic] = useState(null);

  // Escape key closes help
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (activeTopic) setActiveTopic(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTopic, onClose]);

  const goBack = () => setActiveTopic(null);

  const topic = activeTopic ? TOPICS.find(t => t.id === activeTopic) : null;

  return (
    <div className="help-overlay" onClick={onClose}>
      <div className="help-container" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="help-header">
          {topic ? (
            <button className="help-back-btn" onClick={goBack}>← Topics</button>
          ) : (
            <div className="help-header-title">
              <span className="help-header-icon">📖</span>
              Site2Scope Help
            </div>
          )}
          <button className="help-close-btn" onClick={onClose} aria-label="Close help">✕</button>
        </div>

        {/* Content */}
        <div className="help-body">
          {!topic ? (
            /* ── Table of contents ── */
            <div className="help-toc">
              <p className="help-toc-intro">Quick reference guides for getting the most out of Site2Scope.</p>
              <div className="help-toc-grid">
                {TOPICS.map(t => (
                  <button key={t.id} className="help-toc-card" onClick={() => setActiveTopic(t.id)}>
                    <div className="help-toc-card-icon" style={{ background: t.color }}>{t.icon}</div>
                    <div className="help-toc-card-body">
                      <div className="help-toc-card-title">{t.title}</div>
                      <div className="help-toc-card-sub">{t.subtitle}</div>
                    </div>
                    <span className="help-toc-card-arrow">→</span>
                  </button>
                ))}
              </div>
              <div className="help-footer-brand">
                <span>Site2Scope</span> · Higgins Coatings · Internal Use Only
              </div>
            </div>
          ) : (
            /* ── Topic page ── */
            <div className="help-page">
              <div className="help-page-header" style={{ borderColor: topic.color }}>
                <span className="help-page-icon">{topic.icon}</span>
                <div>
                  <h2 className="help-page-title">{topic.title}</h2>
                  <p className="help-page-sub">{topic.subtitle}</p>
                </div>
              </div>
              <div className="help-page-content">
                <topic.content />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
